import { useCallback, useEffect, useRef } from "react";

import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import DefaultLineOptionsDropdown from "@/components/tiptap/utils/DefaultLineOptionsDropdown";
import { setLineWrapperGetPos } from "@/components/tiptap/utils/line-wrapper/lineWrapperGetPos";
import { LOCK_INDICATOR_CLASS } from "@/components/tiptap/utils/line-wrapper/specs/lockIndicator";
import { twMerge } from "@/utils/twMerge";

import type { LineMenuProps } from "@/components/tiptap/utils/LineWrapperView";

type Props = Pick<LineMenuProps, "doc" | "getPos" | "onOpenChange"> & {
  className?: string;
  autoOpen?: boolean;
};

/**
 * Static gutter lock icon for React node views. Clicks are handled by
 * LockIndicatorsOverlay, which floats the block menu over the icon from outside
 * the editor DOM, so PM selection redraws can't close it.
 */
export function LockIndicatorStatic({ getPos }: Pick<LineMenuProps, "getPos">) {
  const t = useTranslations("tiptap");

  const indicatorRef = useCallback(
    (el: HTMLSpanElement | null) => {
      if (el) {
        setLineWrapperGetPos(el, getPos);
      }
    },
    [getPos],
  );

  return (
    <span className="absolute inset-y-0 left-0 flex items-center select-none">
      <span
        ref={indicatorRef}
        data-lock-indicator
        role="button"
        aria-label={t("lockedBlock")}
        className={twMerge("lock-indicator-static", LOCK_INDICATOR_CLASS)}
      >
        <Icon name="LockLocked" className="w-4 h-4 flex-shrink-0 stroke-2" />
      </span>
    </span>
  );
}

/** Gutter lock icon next to the line number; clicking it opens the block menu. */
export function LockIndicatorButton({ doc, getPos, onOpenChange, className, autoOpen }: Props) {
  const t = useTranslations("tiptap");
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (autoOpen) {
      ref.current?.querySelector("button")?.click();
    }
  }, [autoOpen]);

  return (
    <span
      ref={ref}
      data-lock-indicator
      className={className ?? "absolute inset-y-0 left-0 flex items-center select-none"}
      contentEditable={false}
    >
      <DefaultLineOptionsDropdown
        doc={doc}
        getPos={getPos}
        onOpenChange={onOpenChange}
        anchor={{ to: "bottom start", gap: "4px" }}
        toggleButtonLabel={t("lockedBlock")}
        toggleButtonComponent={
          <Icon name="LockLocked" className="w-4 h-4 flex-shrink-0 stroke-2" />
        }
        buttonClassName={twMerge(
          LOCK_INDICATOR_CLASS,
          "data-[open]:bg-blue-100 dark:data-[open]:bg-blue-900 data-[open]:text-icon-active",
        )}
      />
    </span>
  );
}
